import React, { useState } from "react";
import axios from "axios";
import { Button, Input } from "../../shared/ui";
import { Save, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline editor for a machine_usage record — admin / manager / office_staff /
 * supervisor can amend dates, KM/hour readings, operator, job, notes.
 * Writes via PUT /api/machines/{machine_id}/usage/{usage_id}.
 */
export const MachineUsageEditor = ({ usage, machineId, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    start_date: usage.start_date || "",
    actual_end_date: usage.actual_end_date || "",
    starting_hours: usage.starting_hours ?? "",
    ending_hours: usage.ending_hours ?? "",
    starting_kms: usage.starting_kms ?? "",
    ending_kms: usage.ending_kms ?? "",
    client_name: usage.client_name || "",
    location: usage.location || "",
    notes: usage.notes || "",
    status: usage.status || "completed",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = {};
    for (const k of Object.keys(form)) {
      const orig = usage[k] ?? "";
      const cur = form[k] ?? "";
      if (String(orig) === String(cur)) continue;
      // numeric fields
      if (["starting_hours", "ending_hours", "starting_kms", "ending_kms"].includes(k)) {
        payload[k] = cur === "" ? null : parseFloat(cur);
      } else {
        payload[k] = cur === "" ? null : cur;
      }
    }
    if (!Object.keys(payload).length) {
      toast.info("Nothing to save");
      onCancel?.();
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/machines/${machineId}/usage/${usage.id}`, payload);
      toast.success("Usage updated");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update usage");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2 p-3 bg-orange-50 border-2 border-orange-300 rounded-lg" data-testid="machine-usage-editor">
      <div className="text-xs font-bold uppercase text-orange-700">Editing usage record</div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Start Date</label>
          <Input type="date" value={form.start_date?.slice(0,10) || ""} onChange={(e) => set("start_date", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-start-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">End Date</label>
          <Input type="date" value={form.actual_end_date?.slice(0,10) || ""} onChange={(e) => set("actual_end_date", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-end-date" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Start Hours</label>
          <Input type="number" step="0.1" value={form.starting_hours} onChange={(e) => set("starting_hours", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-start-hours" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">End Hours</label>
          <Input type="number" step="0.1" value={form.ending_hours} onChange={(e) => set("ending_hours", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-end-hours" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Start KM</label>
          <Input type="number" step="0.1" value={form.starting_kms} onChange={(e) => set("starting_kms", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-start-km" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">End KM</label>
          <Input type="number" step="0.1" value={form.ending_kms} onChange={(e) => set("ending_kms", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-end-km" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Client</label>
          <Input value={form.client_name} onChange={(e) => set("client_name", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-client" />
        </div>
        <div>
          <label className="text-[10px] uppercase font-bold text-slate-500">Location</label>
          <Input value={form.location} onChange={(e) => set("location", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-location" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase font-bold text-slate-500">Notes</label>
        <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} className="h-9 text-sm" data-testid="usage-edit-notes" />
      </div>
      <div className="flex gap-2 justify-end pt-1">
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving} data-testid="usage-edit-cancel">
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" variant="secondary" onClick={save} disabled={saving} data-testid="usage-edit-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default MachineUsageEditor;
